/**
 * @file permissions.js
 * @description Centralized definition of roles, permissions, and default role permissions for frontend.
 * Must be kept in sync with backend permissions.js
 * 
 * ROLES:
 * - SUPERADMIN: System administrator (backoffice only)
 * - OWNER: Business owner (all permissions, not editable)
 * - ADMIN: Delegated administrator (configurable permissions)
 * - EMPLOYEE: Sales staff (configurable permissions)
 */

export const ROLES = {
  SUPERADMIN: 'superadmin',
  OWNER: 'owner',
  ADMIN: 'admin',
  EMPLOYEE: 'employee'
};

/**
 * All available permissions in the system, grouped by module.
 */
export const PERMISSIONS = {
  // ==================== BACKOFFICE (SUPERADMIN only) ====================
  VIEW_ADMIN_DASHBOARD: 'view_admin_dashboard',
  MANAGE_ALL_BUSINESSES: 'manage_all_businesses',
  IMPERSONATE_BUSINESS: 'impersonate_business',
  OVERRIDE_PLAN: 'override_plan',
  MANAGE_PLANS: 'manage_plans',
  VIEW_CONTACT_MESSAGES: 'view_contact_messages',

  // ==================== SALES ====================
  VIEW_SALES: 'view_sales',
  CREATE_SALES: 'create_sales',
  EDIT_SALES: 'edit_sales',
  DELETE_SALES: 'delete_sales',
  VIEW_SALES_STATS: 'view_sales_stats',

  // ==================== PRODUCTS ====================
  VIEW_PRODUCTS: 'view_products',
  CREATE_PRODUCTS: 'create_products',
  EDIT_PRODUCTS: 'edit_products',
  DELETE_PRODUCTS: 'delete_products',

  // ==================== CLIENTS ====================
  VIEW_CLIENTS: 'view_clients',
  CREATE_CLIENTS: 'create_clients',
  EDIT_CLIENTS: 'edit_clients',
  DELETE_CLIENTS: 'delete_clients',

  // ==================== SUPPLIERS ====================
  VIEW_SUPPLIERS: 'view_suppliers',
  CREATE_SUPPLIERS: 'create_suppliers',
  EDIT_SUPPLIERS: 'edit_suppliers',
  DELETE_SUPPLIERS: 'delete_suppliers',

  // ==================== EXPENSES ====================
  VIEW_EXPENSES: 'view_expenses',
  CREATE_EXPENSES: 'create_expenses',
  EDIT_EXPENSES: 'edit_expenses',
  DELETE_EXPENSES: 'delete_expenses',

  // ==================== INCOMES (otros ingresos) ====================
  VIEW_INCOMES: 'view_incomes',
  CREATE_INCOMES: 'create_incomes',
  EDIT_INCOMES: 'edit_incomes',
  DELETE_INCOMES: 'delete_incomes',

  // ==================== CASH REGISTER ====================
  VIEW_CASH_REGISTER: 'view_cash_register',
  OPEN_CASH_REGISTER: 'open_cash_register',
  CLOSE_CASH_REGISTER: 'close_cash_register',
  MANAGE_CASH_REGISTER_STATIONS: 'manage_cash_register_stations',

  // ==================== DASHBOARD ====================
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_FINANCIAL_SUMMARY: 'view_financial_summary',

  // ==================== SETTINGS ====================
  VIEW_BUSINESS_SETTINGS: 'view_business_settings',
  EDIT_BUSINESS_SETTINGS: 'edit_business_settings',
  MANAGE_USERS: 'manage_users',
  CHANGE_OWN_PASSWORD: 'change_own_password',
  DELETE_USERS: 'delete_users',

  // ==================== QUOTES ====================
  VIEW_QUOTES: 'view_quotes',
  CREATE_QUOTES: 'create_quotes',
  EDIT_QUOTES: 'edit_quotes',
  DELETE_QUOTES: 'delete_quotes'
};

/**
 * Permissions grouped by module for UI display.
 */
export const PERMISSION_GROUPS = {
  sales: {
    labelKey: 'permissionGroups.sales',
    permissions: [
      PERMISSIONS.VIEW_SALES,
      PERMISSIONS.CREATE_SALES,
      PERMISSIONS.EDIT_SALES,
      PERMISSIONS.DELETE_SALES,
      PERMISSIONS.VIEW_SALES_STATS
    ]
  },
  products: {
    labelKey: 'permissionGroups.products',
    permissions: [
      PERMISSIONS.VIEW_PRODUCTS,
      PERMISSIONS.CREATE_PRODUCTS,
      PERMISSIONS.EDIT_PRODUCTS,
      PERMISSIONS.DELETE_PRODUCTS
    ]
  },
  clients: {
    labelKey: 'permissionGroups.clients',
    permissions: [
      PERMISSIONS.VIEW_CLIENTS,
      PERMISSIONS.CREATE_CLIENTS,
      PERMISSIONS.EDIT_CLIENTS,
      PERMISSIONS.DELETE_CLIENTS
    ]
  },
  suppliers: {
    labelKey: 'permissionGroups.suppliers',
    permissions: [
      PERMISSIONS.VIEW_SUPPLIERS,
      PERMISSIONS.CREATE_SUPPLIERS,
      PERMISSIONS.EDIT_SUPPLIERS,
      PERMISSIONS.DELETE_SUPPLIERS
    ]
  },
  expenses: {
    labelKey: 'permissionGroups.expenses',
    permissions: [
      PERMISSIONS.VIEW_EXPENSES,
      PERMISSIONS.CREATE_EXPENSES,
      PERMISSIONS.EDIT_EXPENSES,
      PERMISSIONS.DELETE_EXPENSES
    ]
  },
  incomes: {
    labelKey: 'permissionGroups.incomes',
    permissions: [
      PERMISSIONS.VIEW_INCOMES,
      PERMISSIONS.CREATE_INCOMES,
      PERMISSIONS.EDIT_INCOMES,
      PERMISSIONS.DELETE_INCOMES
    ]
  },
  cashRegister: {
    labelKey: 'permissionGroups.cashRegister',
    permissions: [
      PERMISSIONS.VIEW_CASH_REGISTER,
      PERMISSIONS.OPEN_CASH_REGISTER,
      PERMISSIONS.CLOSE_CASH_REGISTER,
      PERMISSIONS.MANAGE_CASH_REGISTER_STATIONS
    ]
  },
  dashboard: {
    labelKey: 'permissionGroups.dashboard',
    permissions: [
      PERMISSIONS.VIEW_DASHBOARD,
      PERMISSIONS.VIEW_FINANCIAL_SUMMARY
    ]
  },
  settings: {
    labelKey: 'permissionGroups.settings',
    permissions: [
      PERMISSIONS.VIEW_BUSINESS_SETTINGS,
      PERMISSIONS.EDIT_BUSINESS_SETTINGS,
      PERMISSIONS.MANAGE_USERS
    ]
  },
  quotes: {
    labelKey: 'permissionGroups.quotes',
    permissions: [
      PERMISSIONS.VIEW_QUOTES,
      PERMISSIONS.CREATE_QUOTES,
      PERMISSIONS.EDIT_QUOTES,
      PERMISSIONS.DELETE_QUOTES
    ]
  }
};

/**
 * Default permissions for each role.
 */
export const rolePermissions = {
  // SUPERADMIN gets all permissions: backoffice + all business-level permissions
  [ROLES.SUPERADMIN]: Object.values(PERMISSIONS),

  [ROLES.OWNER]: Object.values(PERMISSIONS).filter(p => ![
    'view_admin_dashboard',
    'manage_all_businesses',
    'impersonate_business',
    'override_plan',
    'manage_plans',
    'view_contact_messages'
  ].includes(p)),

  [ROLES.ADMIN]: [
    PERMISSIONS.VIEW_SALES,
    PERMISSIONS.CREATE_SALES,
    PERMISSIONS.EDIT_SALES,
    PERMISSIONS.DELETE_SALES,
    PERMISSIONS.VIEW_SALES_STATS,
    PERMISSIONS.VIEW_PRODUCTS,
    PERMISSIONS.CREATE_PRODUCTS,
    PERMISSIONS.EDIT_PRODUCTS,
    PERMISSIONS.DELETE_PRODUCTS,
    PERMISSIONS.VIEW_CLIENTS,
    PERMISSIONS.CREATE_CLIENTS,
    PERMISSIONS.EDIT_CLIENTS,
    PERMISSIONS.DELETE_CLIENTS,
    PERMISSIONS.VIEW_SUPPLIERS,
    PERMISSIONS.CREATE_SUPPLIERS,
    PERMISSIONS.EDIT_SUPPLIERS,
    PERMISSIONS.DELETE_SUPPLIERS,
    PERMISSIONS.VIEW_EXPENSES,
    PERMISSIONS.CREATE_EXPENSES,
    PERMISSIONS.EDIT_EXPENSES,
    PERMISSIONS.DELETE_EXPENSES,
    PERMISSIONS.VIEW_INCOMES,
    PERMISSIONS.CREATE_INCOMES,
    PERMISSIONS.EDIT_INCOMES,
    PERMISSIONS.DELETE_INCOMES,
    PERMISSIONS.VIEW_CASH_REGISTER,
    PERMISSIONS.OPEN_CASH_REGISTER,
    PERMISSIONS.CLOSE_CASH_REGISTER,
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_FINANCIAL_SUMMARY,
    PERMISSIONS.VIEW_BUSINESS_SETTINGS,
    PERMISSIONS.EDIT_BUSINESS_SETTINGS,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.CHANGE_OWN_PASSWORD,
    PERMISSIONS.VIEW_QUOTES,
    PERMISSIONS.CREATE_QUOTES,
    PERMISSIONS.EDIT_QUOTES,
    PERMISSIONS.DELETE_QUOTES
  ],

  [ROLES.EMPLOYEE]: [
    PERMISSIONS.VIEW_SALES,
    PERMISSIONS.CREATE_SALES,
    PERMISSIONS.EDIT_SALES,
    PERMISSIONS.VIEW_PRODUCTS,
    PERMISSIONS.VIEW_CLIENTS,
    PERMISSIONS.CREATE_CLIENTS,
    PERMISSIONS.EDIT_CLIENTS,
    PERMISSIONS.VIEW_CASH_REGISTER,
    PERMISSIONS.OPEN_CASH_REGISTER,
    PERMISSIONS.CLOSE_CASH_REGISTER,
    PERMISSIONS.CHANGE_OWN_PASSWORD
  ]
};

/**
 * Permission labels for UI display (i18n keys).
 */
export const PERMISSION_LABELS = {
  [PERMISSIONS.VIEW_SALES]: 'permissions.view_sales',
  [PERMISSIONS.CREATE_SALES]: 'permissions.create_sales',
  [PERMISSIONS.EDIT_SALES]: 'permissions.edit_sales',
  [PERMISSIONS.DELETE_SALES]: 'permissions.delete_sales',
  [PERMISSIONS.VIEW_SALES_STATS]: 'permissions.view_sales_stats',
  [PERMISSIONS.VIEW_PRODUCTS]: 'permissions.view_products',
  [PERMISSIONS.CREATE_PRODUCTS]: 'permissions.create_products',
  [PERMISSIONS.EDIT_PRODUCTS]: 'permissions.edit_products',
  [PERMISSIONS.DELETE_PRODUCTS]: 'permissions.delete_products',
  [PERMISSIONS.VIEW_CLIENTS]: 'permissions.view_clients',
  [PERMISSIONS.CREATE_CLIENTS]: 'permissions.create_clients',
  [PERMISSIONS.EDIT_CLIENTS]: 'permissions.edit_clients',
  [PERMISSIONS.DELETE_CLIENTS]: 'permissions.delete_clients',
  [PERMISSIONS.VIEW_SUPPLIERS]: 'permissions.view_suppliers',
  [PERMISSIONS.CREATE_SUPPLIERS]: 'permissions.create_suppliers',
  [PERMISSIONS.EDIT_SUPPLIERS]: 'permissions.edit_suppliers',
  [PERMISSIONS.DELETE_SUPPLIERS]: 'permissions.delete_suppliers',
  [PERMISSIONS.VIEW_EXPENSES]: 'permissions.view_expenses',
  [PERMISSIONS.CREATE_EXPENSES]: 'permissions.create_expenses',
  [PERMISSIONS.EDIT_EXPENSES]: 'permissions.edit_expenses',
  [PERMISSIONS.DELETE_EXPENSES]: 'permissions.delete_expenses',
  [PERMISSIONS.VIEW_INCOMES]: 'permissions.view_incomes',
  [PERMISSIONS.CREATE_INCOMES]: 'permissions.create_incomes',
  [PERMISSIONS.EDIT_INCOMES]: 'permissions.edit_incomes',
  [PERMISSIONS.DELETE_INCOMES]: 'permissions.delete_incomes',
  [PERMISSIONS.VIEW_CASH_REGISTER]: 'permissions.view_cash_register',
  [PERMISSIONS.OPEN_CASH_REGISTER]: 'permissions.open_cash_register',
  [PERMISSIONS.CLOSE_CASH_REGISTER]: 'permissions.close_cash_register',
  [PERMISSIONS.MANAGE_CASH_REGISTER_STATIONS]: 'permissions.manage_cash_register_stations',
  [PERMISSIONS.VIEW_DASHBOARD]: 'permissions.view_dashboard',
  [PERMISSIONS.VIEW_FINANCIAL_SUMMARY]: 'permissions.view_financial_summary',
  [PERMISSIONS.VIEW_BUSINESS_SETTINGS]: 'permissions.view_business_settings',
  [PERMISSIONS.EDIT_BUSINESS_SETTINGS]: 'permissions.edit_business_settings',
  [PERMISSIONS.MANAGE_USERS]: 'permissions.manage_users',
  [PERMISSIONS.CHANGE_OWN_PASSWORD]: 'permissions.change_own_password',
  [PERMISSIONS.VIEW_QUOTES]: 'permissions.view_quotes',
  [PERMISSIONS.CREATE_QUOTES]: 'permissions.create_quotes',
  [PERMISSIONS.EDIT_QUOTES]: 'permissions.edit_quotes',
  [PERMISSIONS.DELETE_QUOTES]: 'permissions.delete_quotes'
};
